import { createContext, useContext, useEffect, useMemo, useState, type PropsWithChildren } from 'react';

export type Language = 'en' | 'mm';

const LANGUAGE_KEY = 'app_language';

const translations = {
  en: {
    common: {
      loading: 'Loading...',
      dismiss: 'Dismiss',
      retry: 'Try Again',
      close: 'Close',
      success: 'Success',
      failed: 'Failed',
      info: 'Info',
      warning: 'Warning',
      guest: 'Guest',
      balance: 'Balance',
      amount: 'Amount',
      account: 'Account',
      package: 'Package',
      total: 'Total',
      totalPayment: 'Total Payment',
      orderId: 'Order ID',
      key: 'Key',
      item: 'Item',
      game: 'Game',
      provider: 'Provider',
      type: 'Type',
      region: 'Region',
      playerName: 'Player Name',
      playerId: 'Player ID',
      unknown: 'Unknown',
      completed: 'Completed',
      refresh: 'Refresh',
      notFoundTitle: 'Oops!',
      somethingWentWrong: 'Something went wrong',
      fields: {
        gameid: 'Game ID',
        zoneid: 'Server ID',
        serverid: 'Server ID',
        server_id: 'Server ID',
        account_id: 'Account ID',
        additional_id: 'Additional ID',
        userid: 'User ID',
        phone: 'Phone',
        msisdn: 'Phone',
        id: 'ID',
        gmail: 'Gmail',
        uid: 'UID',
        contact: 'Contact',
        eamail: 'EA Email',
        supermail: 'Supercell ID Email',
        server: 'Server',
      },
      languages: {
        en: 'ENG',
        mm: 'MM',
      },
      languageNames: {
        en: 'English',
        mm: 'Myanmar',
      },
      login: 'Login',
      cancel: 'Cancel',
      ok: 'OK',
      back: 'Back',
      share: 'Share',
      linkCopied: 'Link copied to clipboard!',
      seeMore: 'See more',
      seeLess: 'See less',
    },
    shell: {
      openMenu: 'Open navigation menu',
      appTitle: 'AMT-TopUp Services',
      gameDetail: 'Top-up Details',
      titles: {
        home: 'Games',
        mlChecker: 'ML Region Checker',
        contactAdmin: 'Contact Admin',
        privacy: 'Privacy Policy',
        history: 'History',
        profile: 'Profile',
        topup: 'Topup',
        spin: 'Lucky Spin',
        devices: 'Device Management',
        leaderboard: 'Leaderboard',
        dataPack: 'Data & Pack',
      },
      drawer: {
        home: 'Home',
        games: 'Games',
        dataPack: 'Data & Pack',
        topup: 'Topup',
        history: 'History',
        profile: 'Profile',
        mlChecker: 'ML Region Checker',
        support: 'Contact Admin',
        installApp: 'Install App',
        privacy: 'Privacy Policy',
        logout: 'Logout',
      },
      install: {
        unavailable: 'Open this site in Chrome and use "Install App" or "Add to Home Screen".',
        accepted: 'Install prompt opened.',
        dismissed: 'Install was cancelled.',
        installed: 'App installed successfully.',
      },
    },
    contactAdmin: {
      title: 'Contact Admin',
      otpRequired: 'OTP Required',
      otpMessage: 'OTP is required to complete your purchase. Please contact admin through the links below.',
    },
    auth: {
      title: 'AMT-TopUp Services',
      welcomeBack: 'Welcome Back! Sign in to continue',
      createAccountLead: 'Create an account to get started',
      login: 'Login',
      signup: 'Sign up',
      forgotPassword: 'Forgot password?',
      forgotPasswordLead: 'Reset your password with email verification',
      sendResetLink: 'Send reset link',
      backToLogin: 'Back to login',
      loadingTurnstile: 'Loading security check...',
      turnstileRequired: 'Please complete the security check.',
      turnstileExpired: 'Security check expired. Please complete it again.',
      turnstileRetry: 'Security check failed. Please try again.',
      turnstileUnavailable: 'Security check is unavailable right now. Please try again later.',
      emailLabel: 'Email',
      emailPlaceholder: 'Enter your email address',
      passwordLabel: 'Password',
      passwordPlaceholder: 'Enter your password',
      fullNameLabel: 'Full Name',
      fullNamePlaceholder: 'Enter your full name',
      createPasswordPlaceholder: 'Create a password (min 6 chars)',
      passwordHelper: 'At least 6 characters',
      signIn: 'Sign In',
      signingIn: 'Signing in...',
      createAccount: 'Create Account',
      creatingAccount: 'Creating account...',
      errors: {
        emailRequired: 'Email is required',
        passwordRequired: 'Password is required',
        nameRequired: 'Name is required',
        passwordShort: 'Password must be at least 6 characters',
      },
      toasts: {
        loginSuccess: 'Welcome back! Login successful.',
        loginFailed: 'Login Failed',
        registerSuccess: 'Account created successfully!',
        registerFailed: 'Registration Failed',
        resetFailed: 'Reset Request Failed',
      },
    },
    home: {
      noPromotions: 'No promotions available right now',
      popularGames: 'Popular Games',
      gamesAvailable: (count: number) => `${count} games available`,
      couldntLoadGames: "Couldn't load games",
      instantTopup: 'Instant',
      estimatedTopup: '3 Min',
      promotionAlt: 'promotion',
      slideLabel: (index: number) => `Go to slide ${index}`,
      searchGames: 'Search games...',
      noSearchResults: 'No games found',
      event: 'EVENT',
      posts: 'posts',
    },
    event: {
      title: 'Event',
      notAvailable: 'No active event at the moment.',
      noPosts: 'No posts yet. Check back later!',
    },
    dataPack: {
      title: 'Data & Pack',
      noPromotions: 'No promotions available right now',
      popularDataPacks: 'Popular Data & Packs',
      popularPacks: 'Popular Packs',
      popularProviders: 'Popular Providers',
      wifiServices: 'WiFi Services',
      couldntLoad: "Couldn't load providers",
      searchPlaceholder: 'Search providers...',
      noSearchResults: 'No providers found',
      empty: 'No providers available',
      providerNotFound: 'Provider not found',
      selectCategory: 'Select Category',
      selectType: 'Select Type',
      chooseType: 'Choose a type...',
      items: 'items',
      discount: 'discount',
      placeOrder: 'Place Order',
      orderSuccess: 'Order placed successfully!',
      orderFailed: 'Order failed',
      selectedItem: 'Selected',
    },
    notifications: {
      title: 'Notifications',
      markAllRead: 'Mark all read',
      loadError: 'Failed to load notifications',
      empty: 'No notifications yet',
      defaultTitle: 'Notification',
    },
    history: {
      tabs: {
        orders: 'Orders',
        topups: 'Topups',
      },
      purchase: {
        couldntLoad: "Couldn't load orders",
        empty: "You haven't placed any orders yet. Browse our services and make your first purchase!",
        browseGames: 'Browse Services',
      },
      topup: {
        couldntLoad: "Couldn't load topups",
        empty: 'No topup history yet. Add funds to your wallet to see them here.',
        topupNow: 'Top Up Now',
        paymentMethod: 'Payment Method',
        rejectionReason: 'Rejection Reason',
      },
    },
    topup: {
      noPhoneLinked: 'No phone linked. Please log in again.',
      userNotFound: 'User not found. Please log in again.',
      availableHours: 'Service hours: 9:00 AM to 9:00 PM.',
      notices: [
        'If your top-up does not arrive, please contact Admin.',
        'Upload the screenshot proof immediately after you complete the payment.',
        'Screenshots older than 24 hours will not be checked or refunded.',
        'Write your account name in the note.',
      ],
      selectMethod: 'Select a payment method',
      enterAmountAndScreenshot: 'Enter amount and attach payment screenshot',
      enterAmount: 'Enter amount',
      attachScreenshot: 'Attach payment screenshot',
      uploading: 'Uploading payment...',
      receivedSlip: 'We received your payment slip.',
      currentBalance: 'Current Balance',
      paymentInfo: 'Payment Info',
      method: 'Method',
      name: 'Name',
      copyAccount: 'Copy account',
      accountCopied: 'Account copied',
      amountPlaceholder: 'Amount',
      chooseScreenshot: 'Choose screenshot',
      browse: 'Browse',
      submitTopup: 'Submit top up',
      closeDialog: 'Close',
      accountNumber: 'Account Number',
    },
    profile: {
      defaultName: 'User',
      orderHistory: 'Order history',
      topupHistory: 'Topup history',
      privacy: 'Privacy Policy',
      updateProfile: 'Update Profile',
      namePlaceholder: 'Name',
      phonePlaceholder: 'Email',
      saveChanges: 'Save changes',
      changePassword: 'Change password',
      currentPassword: 'Current password',
      newPassword: 'New password',
      changePasswordButton: 'Change password',
      logout: 'Logout',
      deleteAccount: 'Delete account',
      updatingProfile: 'Updating profile...',
      profileUpdated: 'Profile updated successfully.',
      updatingPassword: 'Updating password...',
      passwordUpdated: 'Password updated successfully.',
      deletingAccount: 'Deleting account...',
      accountDeleted: 'Account deleted.',
      pleaseWait: 'Please wait',
      confirmDeleteTitle: 'Delete account?',
      confirmDeleteBody: 'This action cannot be undone.',
      cancel: 'Cancel',
      confirmDelete: 'Yes, delete',
      profileAlt: 'Profile',
      emailFallback: 'user@example.com',
      topUp: 'Top Up',
      logOut: 'Log Out',
      historyOrdersTab: 'Order History',
      historyTopupsTab: 'Topup History',
      noOrdersYet: 'No orders yet',
      noTopupsYet: 'No topups yet',
      playerLabel: 'Player',
      changePasswordTitle: 'Change Password',
    },
    mlChecker: {
      title: 'ML Region Checker',
      gameId: 'Game ID',
      zoneId: 'Server ID',
      checkRegion: 'Check Region',
      nickname: 'Nickname',
      region: 'Region',
      lastLogin: 'Last login',
      found: 'Found',
      yes: 'Yes',
      no: 'No',
    },
    gameDetail: {
      notFound: 'Service not found',
      topupAvailable: 'Top up available',
      instantDelivery: 'Instant Delivery',
      estimatedDelivery: '3 Min',
      enterAccountDetails: 'Enter Account Details',
      accountDetailsCopy: 'Find your Game ID and Server ID in your game profile.',
      selectPackage: 'Select Package',
      selectPackageCopy: 'Choose the package you want to purchase.',
      couldntLoadPackages: "Couldn't load packages",
      noPackages: 'No packages available yet. Please check back later.',
      goBack: 'Go Back',
      checkAccount: 'Check Account',
      purchase: 'Purchase',
      buyNow: 'Buy Now',
      verifyingAccount: 'Verifying account...',
      processingOrder: 'Processing your order...',
      confirmOrder: 'Confirm Order',
      reviewOrder: 'Review Order',
      reviewOrderCopy: 'Please review your order details before confirming',
      placeOrder: 'Place Order',
      confirmPurchase: 'Confirm Purchase',
      purchaseCompleted: 'Purchase completed successfully!',
      purchaseFailed: 'Purchase Failed',
      processingTitle: 'Processing',
      processingMessage: 'Your order is being processed. Check your history for updates.',
      missingField: (field: string) => `Please enter ${field}.`,
      invalidProduct: 'Invalid product. Please refresh and try again.',
      accountNotFoundTitle: 'Account Not Found',
      accountNotFound: 'Account not found. Please check your IDs.',
      otpRequiredMessage: 'Order တင်ပြီးတာနဲ့ Otp ပေးရန် Admin ဆီ ဆက်သွယ်ပါ။',
        placeholders: {
        gameid: 'e.g. 12345678',
        zoneid: 'e.g. 1234',
        gmail: 'Enter your Gmail',
        userid: 'Enter user ID',
        uid: 'Enter UID',
        contact : 'Enter contact account (tg or viber)',
        eamail: 'Enter your EA account email',
        supermail: 'Enter your Supercell ID email',
        hi: 'Enter your hunter ID',
        generic: (label: string) => `Enter your ${label}`,
      },
    },
    privacy: {
      title: 'Privacy Policy',
      sections: [
        '1. Introduction\nWelcome to AMT-TopUp Services ("we," "our," or "us"). This Privacy Policy explains how we collect, use, and protect your information when you use our mobile application ("App"). By using our App, you agree to the practices described in this Privacy Policy.',
        '2. Information We Collect\nOur App does not intentionally collect sensitive personal data. However, to provide our services, we may collect and process the following information:',
        '2.1 User Authentication\n- Email address and password for login\n- Account verification and login token\n- Basic profile information (name)\n- Device information for security (non-personal)\n\nThis information is required to authenticate your account, secure your access, and prevent fraud.',
        '2.2 Transactions & Wallet\n- Top-up history\n- Purchase/order history\n- Uploaded payment screenshots (for verification)\n\nWe use this information only to process your top-ups, verify your payments, and deliver your orders.',
        '3. How We Use Your Information\n- To authenticate and manage your account\n- To process wallet top-ups and purchases\n- To deliver game items through official channels\n- To provide customer support\n- To prevent fraud and maintain app security\n\nWe do not sell, rent, or trade your personal information. We only share your data with trusted payment or top-up partners when necessary to complete your order.',
        '4. Game Item Reselling Disclaimer\nWe are a third-party game item reseller sourcing products from official distributors such as Codashop or other verified providers. All top-ups and deliveries are completed via official channels. This app does not bypass or modify Google Play Billing; all payments take place outside the app through external payment pages.',
        '5. External Payments\nAll payments are processed outside the app (browser or external payment apps). Any screenshots or uploads you provide are used only to verify your payment and prevent fraudulent orders.',
        '6. Intellectual Property\nAll game logos, banners, and item images belong to their respective owners. AMT-TopUp Services is an independent reseller and is not affiliated with, endorsed by, or officially connected to any game publisher or company.',
        '7. User Rights\nYou may request to:\n- Delete your account\n- Access the data we store about you\n- Update or correct your information\n\nYou can delete your account via the app settings or by contacting us directly.',
        '8. Data Security\nWe implement security measures to protect your information, including data encryption and secured servers. While we take reasonable precautions, no system can be 100% secure.',
        '9. Contact Us\nIf you have any questions about this Privacy Policy or your data, please contact us at:',
      ],
    },
    maintenance: {
      loading: 'Loading',
      retry: 'Retry',
    },
    spin: {
      title: 'Lucky Spin',
      loading: 'Loading...',
      spinsAvailable: '{count} spins available',
      spinNow: 'SPIN NOW',
      spinning: 'Spinning...',
      noSpins: 'No Spins Available',
      earnSpins: 'Top up {amount} MMK or more to earn spins!',
      newBalance: 'New Balance',
      youWon: 'You Won',
      betterLuckNextTime: 'Better luck next time!',
      spinRemaining: (count: number) => `${count} spin${count > 1 ? 's' : ''} remaining`,
      noSpinsRemaining: 'No more spins available',
      spinAgain: 'Spin Again',
    },
    devices: {
      title: 'Device Management',
      subtitle: 'Manage your connected devices',
      loading: 'Loading devices...',
      noDevices: 'No devices found',
      currentDevice: 'Current Device',
      added: 'Added',
      lastSeen: 'Last seen',
      remove: 'Remove',
      infoText: 'Removing a device will sign it out of your account.',
      confirmRemoveTitle: 'Remove Device?',
      confirmRemoveText: 'This device will be signed out of your account. Are you sure?',
    },
    leaderboard: {
      title: 'Leaderboard',
      subtitle: 'Top spenders this month',
      loading: 'Loading leaderboard...',
      noData: 'No data available',
    },
    chat: {
      title: 'Chat',
      comingSoon: 'Coming Soon',
      description: 'Chat feature will be available soon. Stay tuned!',
      supportTitle: 'Support Chat',
      online: 'Online',
      offline: 'Offline',
      connecting: 'Connecting...',
      reconnecting: 'Reconnecting...',
      ticket: 'Ticket',
      typeMessage: 'Type a message...',
      startConversation: 'Start a Conversation',
      supportGreeting: 'Our support team is here to help you!',
      loginRequired: 'Login Required',
      loginMessage: 'Please login to access chat support',
    },
  },
  mm: {
    common: {
      loading: 'ခဏစောင့်ပါ...',
      dismiss: 'ပယ်ဖျက်မည်',
      retry: 'ပြန်စမ်းမည်',
      close: 'ပိတ်မည်',
      success: 'အောင်မြင်ပါသည်',
      failed: 'မအောင်မြင်ပါ',
      info: 'အချက်အလက်',
      warning: 'သတိပေးချက်',
      guest: 'ဧည့်သည်',
      balance: 'လက်ကျန်ငွေ',
      amount: 'ပမာဏ',
      account: 'အကောင့်',
      package: 'ပက်ကေ့ချ်',
      total: 'စုစုပေါင်း',
      totalPayment: 'ကျသင့်ငွေ စုစုပေါင်း',
      orderId: 'အော်ဒါနံပါတ်',
      key: 'Key',
      item: 'ပစ္စည်း',
      game: 'ဂိမ်း',
      provider: 'Provider',
      type: 'Type',
      region: 'Region',
      playerName: 'Player Name',
      playerId: 'Player ID',
      unknown: 'မသိရှိပါ',
      completed: 'ပြီးဆုံးသည်',
      refresh: 'ပြန်လည်တင်မည်',
      notFoundTitle: 'မတွေ့ရှိပါ',
      somethingWentWrong: 'တစ်ခုခု မှားယွင်းနေပါသည်',
      fields: {
        gameid: 'ဂိမ်း ID',
        zoneid: 'ဆာဗာ ID',
        serverid: 'ဆာဗာ ID',
        server_id: 'ဆာဗာ ID',
        account_id: 'အကောင့် ID',
        additional_id: 'Additional ID',
        userid: 'အသုံးပြုသူ ID',
        phone: 'ဖုန်းနံပါတ်',
        msisdn: 'ဖုန်းနံပါတ်',
        id: 'ID',
        gmail: 'gmail',
        uid: 'UID',
        contact: 'ဆက်သွယ်ရန်',
        eamail: 'Ea Email',
        supermail: 'Supercell email',
        server: 'server',
      },
      languages: { en: 'ENG', mm: 'MM' },
      languageNames: { en: 'English', mm: 'Myanmar' },
      login: 'အကောင့်ဝင်မည်',
      cancel: 'မလုပ်တော့ပါ',
      ok: 'အိုကေ',
      back: 'နောက်သို့',
      share: 'မျှဝေရန်',
      linkCopied: 'လင့်ခ်ကို ကော်ပီကူးပြီးပါပြီ!',
      seeMore: 'seemore',
      seeLess: 'seeless',
    },
    shell: {
      openMenu: 'မီနူးဖွင့်မည်',
      appTitle: 'AMT-TopUp Services',
      gameDetail: 'ငွေဖြည့်ရန် အသေးစိတ်',
      titles: {
        home: 'ဂိမ်းများ',
        mlChecker: 'ML Region စစ်ဆေးရန်',
        contactAdmin: 'Admin ကို ဆက်သွယ်ရန်',
        privacy: 'ကိုယ်ရေးအချက်အလက် မူဝါဒ',
        history: 'မှတ်တမ်း',
        profile: 'ပရိုဖိုင်',
        topup: 'ငွေဖြည့်ရန်',
        spin: 'ကံစမ်းမဲ',
        devices: 'စက်ပစ္စည်းများ စီမံခန့်ခွဲမှု',
        leaderboard: 'အမှတ်ပေးဇယား',
           dataPack: 'Data & Pack',
      },
      drawer: {
        home: 'ပင်မစာမျက်နှာ',
        games: 'ဂိမ်းများ',
         dataPack: 'Data & Pack',
        topup: 'ငွေဖြည့်ရန်',
        history: 'မှတ်တမ်း',
        profile: 'ပရိုဖိုင်',
        mlChecker: 'ML Region စစ်ဆေးရန်',
        support: 'Admin ကို ဆက်သွယ်ရန်',
        installApp: 'App ထည့်သွင်းရန်',
        privacy: 'ကိုယ်ရေးအချက်အလက် မူဝါဒ',
        logout: 'အကောင့်ထွက်မည်',
      },
      install: {
        unavailable: 'Chrome Browser ဖြင့်ဖွင့်ပြီး "Install App" သို့မဟုတ် "Add to Home Screen" ကို အသုံးပြုပါ။',
        accepted: 'Install ပြုလုပ်ရန် ဖွင့်ထားပါသည်။',
        dismissed: 'Install ပြုလုပ်ခြင်းကို ပယ်ဖျက်လိုက်သည်။',
        installed: 'App ကို အောင်မြင်စွာ ထည့်သွင်းပြီးပါပြီ။',
      },
    },
    auth: {
      title: 'AMT-TopUp Services',
      welcomeBack: 'ပြန်လည်ကြိုဆိုပါသည်။ ဆက်လက်အသုံးပြုရန် အကောင့်ဝင်ပေးပါ',
      createAccountLead: 'စတင်အသုံးပြုရန် အကောင့်အသစ် ဖွင့်ပါ',
      login: 'အကောင့်ဝင်မည်',
      signup: 'အကောင့်ဖွင့်မည်',
      forgotPassword: 'စကားဝှက် မေ့နေပါသလား?',
      forgotPasswordLead: 'Email အတည်ပြုမှုဖြင့် စကားဝှက် ပြန်လည်သတ်မှတ်ပါ',
      sendResetLink: 'Reset link ပို့မည်',
      backToLogin: 'အကောင့်ဝင်ရန် ပြန်သွားမည်',
      loadingTurnstile: 'လုံခြုံရေးစစ်ဆေးမှု တင်နေသည်...',
      turnstileRequired: 'လုံခြုံရေးစစ်ဆေးမှုကို အပြီးသတ်ပေးပါ။',
      turnstileExpired: 'လုံခြုံရေးစစ်ဆေးမှု သက်တမ်းကုန်သွားပါသည်။ ထပ်မံပြုလုပ်ပေးပါ။',
      turnstileRetry: 'လုံခြုံရေးစစ်ဆေးမှု မအောင်မြင်ပါ။ ထပ်မံကြိုးစားပေးပါ။',
      turnstileUnavailable: 'လုံခြုံရေးစစ်ဆေးမှု လောလောဆယ် မရနိုင်ပါ။ နောက်မှ ထပ်စမ်းပေးပါ။',
      emailLabel: 'Email',
      emailPlaceholder: 'Email လိပ်စာ ထည့်ပါ',
      passwordLabel: 'စကားဝှက်',
      passwordPlaceholder: 'စကားဝှက် ထည့်ပါ',
      fullNameLabel: 'အမည်အပြည့်အစုံ',
      fullNamePlaceholder: 'အမည် ထည့်ပါ',
      createPasswordPlaceholder: 'စကားဝှက်အသစ် ဖန်တီးပါ (အနည်းဆုံး ၆ လုံး)',
      passwordHelper: 'အနည်းဆုံး စာလုံး ၆ လုံး',
      signIn: 'အကောင့်ဝင်မည်',
      signingIn: 'အကောင့်ဝင်နေသည်...',
      createAccount: 'အကောင့်ဖွင့်မည်',
      creatingAccount: 'အကောင့်ဖန်တီးနေသည်...',
      errors: {
        emailRequired: 'Email လိုအပ်ပါသည်',
        passwordRequired: 'စကားဝှက် လိုအပ်ပါသည်',
        nameRequired: 'အမည် လိုအပ်ပါသည်',
        passwordShort: 'စကားဝှက် အနည်းဆုံး ၆ လုံး ရှိရပါမည်',
      },
      toasts: {
        loginSuccess: 'အကောင့်ဝင်ခြင်း အောင်မြင်ပါသည်',
        loginFailed: 'အကောင့်ဝင်ခြင်း မအောင်မြင်ပါ',
        registerSuccess: 'အကောင့်အသစ် ဖန်တီးခြင်း အောင်မြင်ပါသည်',
        registerFailed: 'အကောင့်ဖန်တီးခြင်း မအောင်မြင်ပါ',
        resetFailed: 'Reset request မအောင်မြင်ပါ',
      },
    },
    home: {
      noPromotions: 'လက်ရှိ ပရိုမိုးရှင်း မရှိသေးပါ',
      popularGames: 'လူကြိုက်များသော ဂိမ်းများ',
      gamesAvailable: (count: number) => `ဂိမ်း ${count} ခု ရှိသည်`,
      couldntLoadGames: 'ဂိမ်းများ မတင်နိုင်ပါ',
      instantTopup: 'မစောင့်ရ',
      estimatedTopup: '3 မိနစ်',
      promotionAlt: 'ပရိုမိုးရှင်း',
      slideLabel: (index: number) => `ဆလိုက် ${index} သို့ သွားရန်`,
      searchGames: 'ဂိမ်းများ ရှာဖွေရန်...',
      noSearchResults: 'ဂိမ်းများ မတွေ့ရှိပါ',
      event: 'EVENTS',
      posts: 'posts',
    },
    event: {
      title: 'EVENTS',
      notAvailable: 'no active event at the moment.',
      noPosts: 'no posts yet. Check back later!',
    },
    dataPack: {
      title: 'ဒေတာ နှင့် ပက်ကေ့ချ်',
      noPromotions: 'လက်ရှိ ပရိုမိုးရှင်း မရှိသေးပါ',
      popularDataPacks: 'လူကြိုက်များသော ဒေတာ နှင့် ပက်ကေ့ချ်များ',
      popularPacks: 'လူကြိုက်များသော ပက်ကေ့ချ်များ',
      popularProviders: 'လူကြိုက်များသော ပံ့ပိုးပေးသူများ',
      wifiServices: 'WiFi ဝန်ဆောင်မှုများ',
      couldntLoad: 'ပံ့ပိုးပေးသူများ မတင်နိုင်ပါ',
      searchPlaceholder: 'ပံ့ပိုးပေးသူများ ရှာဖွေရန်...',
      noSearchResults: 'ပံ့ပိုးပေးသူများ မတွေ့ရှိပါ',
      empty: 'ပံ့ပိုးပေးသူများ မရှိသေးပါ',
      providerNotFound: 'ပံ့ပိုးပေးသူ မတွေ့ရှိပါ',
      selectCategory: 'အမျိုးအစား ရွေးချယ်ပါ',
      selectType: 'အမျိုးအစား ရွေးချယ်ပါ',
      chooseType: 'အမျိုးအစားရွေးပါ...',
      items: 'ပစ္စည်းများ',
      discount: 'လျှော့စျေး',
      placeOrder: 'အော်ဒါတင်ပါ',
      orderSuccess: 'အော်ဒါ အောင်မြင်စွာ တင်ပြီးပါပြီ!',
      orderFailed: 'အော်ဒါ တင်ရန် မအောင်မြင်ပါ',
      selectedItem: 'ရွေးချယ်ထားသော',
    },
    notifications: {
      title: 'အသိပေးချက်များ',
      markAllRead: 'အားလုံးဖတ်ပြီးကြောင်း မှတ်သားမည်',
      loadError: 'အသိပေးချက်များ မတင်နိုင်ပါ',
      empty: 'အသိပေးချက်များ မရှိသေးပါ',
      defaultTitle: 'အသိပေးချက်',
    },
    history: {
      tabs: { orders: 'အော်ဒါများ', topups: 'ငွေဖြည့်မှတ်တမ်း' },
      purchase: {
        couldntLoad: 'အော်ဒါများ မတင်နိုင်ပါ',
        empty: 'ဝယ်ယူမှု မှတ်တမ်း မရှိသေးပါ။ ဝန်ဆောင်မှုများကြည့်ပြီး ပထမဆုံး ဝယ်ယူမှု ပြုလုပ်နိုင်ပါသည်',
        browseGames: 'ဝန်ဆောင်မှုများ ကြည့်မည်',
      },
      topup: {
        couldntLoad: 'ငွေဖြည့်မှတ်တမ်း မတင်နိုင်ပါ',
        empty: 'ငွေဖြည့်မှတ်တမ်း မရှိသေးပါ။ Wallet ထဲသို့ ငွေဖြည့်ပေးပါ',
        topupNow: 'ယခု ငွေဖြည့်မည်',
        paymentMethod: 'ငွေပေးချေမှု နည်းလမ်း',
        rejectionReason: 'ပယ်ချရခြင်း အကြောင်းရင်း',
      },
    },
    topup: {
      noPhoneLinked: 'ဖုန်းနံပါတ် ချိတ်ဆက်မထားပါ။ ပြန်လည်ဝင်ရောက်ပေးပါ',
      userNotFound: 'အသုံးပြုသူ မတွေ့ရှိပါ။ ပြန်လည်ဝင်ရောက်ပေးပါ',
      availableHours: 'ဝန်ဆောင်မှုရရှိနိုင်ချိန် - နံနက် ၉:၀၀ နာရီမှ ည ၉:၀၀ နာရီအထိ',
      notices: [
        'ငွေဖြည့်သွင်းမှု မရောက်ရှိပါက Admin ကို ဆက်သွယ်နိုင်ပါသည်။',
        'ငွေဖြည့်ပြီးသည်နှင့် Screenshot အထောက်အထားကို ချက်ချင်း Upload ပြုလုပ်ပေးပါ။',
        'ငွေဖြည့်ချိန်မှ ၂၄ နာရီ ကျော်လွန်သော Screenshot များကို စစ်ဆေးပေးမည် မဟုတ်သလို ငွေပြန်အမ်းပေးမည် မဟုတ်ပါ။',
        'အကောင့်ဖွင့်ထားသည့် အမည်ကို Note တွင် ရေးပေးပါ။',
      ],
      selectMethod: 'ငွေပေးချေမှု နည်းလမ်း ရွေးချယ်ပါ',
      enterAmountAndScreenshot: 'ငွေပမာဏထည့်ပြီး ငွေပေးချေမှု အထောက်အထား (Screenshot) တင်ပေးပါ',
      enterAmount: 'ငွေပမာဏ ထည့်ပါ',
      attachScreenshot: 'Screenshot တင်ရန်',
      uploading: 'ငွေပေးချေမှု အချက်အလက် တင်နေသည်...',
      receivedSlip: 'သင်၏ ငွေပေးချေမှု အထောက်အထားကို လက်ခံရရှိပါပြီ',
      currentBalance: 'လက်ရှိ လက်ကျန်ငွေ',
      paymentInfo: 'ငွေပေးချေမှု အချက်အလက်',
      method: 'နည်းလမ်း',
      name: 'အမည်',
      copyAccount: 'အကောင့်နံပါတ် ကူးယူရန်',
      accountCopied: 'ကူးယူပြီးပါပြီ',
      amountPlaceholder: 'ငွေပမာဏ',
      chooseScreenshot: 'Screenshot ရွေးချယ်ပါ',
      browse: 'ရှာဖွေရန်',
      submitTopup: 'ငွေဖြည့်ရန် တင်သွင်းမည်',
      closeDialog: 'ပိတ်မည်',
      accountNumber: 'အကောင့်နံပါတ်',
    },
    profile: {
      defaultName: 'အသုံးပြုသူ',
      orderHistory: 'အော်ဒါမှတ်တမ်း',
      topupHistory: 'ငွေဖြည့်မှတ်တမ်း',
      privacy: 'ကိုယ်ရေးအချက်အလက် မူဝါဒ',
      updateProfile: 'ပရိုဖိုင် ပြင်ဆင်ရန်',
      namePlaceholder: 'အမည်',
      phonePlaceholder: 'Email',
      saveChanges: 'သိမ်းဆည်းမည်',
      changePassword: 'စကားဝှက် ပြောင်းလဲရန်',
      currentPassword: 'လက်ရှိ စကားဝှက်',
      newPassword: 'စကားဝှက်အသစ်',
      changePasswordButton: 'စကားဝှက် ပြောင်းမည်',
      logout: 'အကောင့်ထွက်မည်',
      deleteAccount: 'အကောင့် ဖျက်သိမ်းမည်',
      updatingProfile: 'ပရိုဖိုင် ပြင်ဆင်နေသည်...',
      profileUpdated: 'ပရိုဖိုင် ပြင်ဆင်ခြင်း အောင်မြင်ပါသည်',
      updatingPassword: 'စကားဝှက် ပြင်ဆင်နေသည်...',
      passwordUpdated: 'စကားဝှက် ပြောင်းလဲခြင်း အောင်မြင်ပါသည်',
      deletingAccount: 'အကောင့် ဖျက်သိမ်းနေသည်...',
      accountDeleted: 'အကောင့် ဖျက်သိမ်းပြီးပါပြီ',
      pleaseWait: 'ခဏစောင့်ပါ',
      confirmDeleteTitle: 'အကောင့် ဖျက်သိမ်းမည်လား?',
      confirmDeleteBody: 'ဤလုပ်ဆောင်ချက်ကို ပြန်လည်ပြင်ဆင်၍ မရနိုင်ပါ။',
      cancel: 'မဖျက်တော့ပါ',
      confirmDelete: 'ဟုတ်ကဲ့၊ ဖျက်မည်',
      profileAlt: 'ပရိုဖိုင်',
      emailFallback: 'user@example.com',
      topUp: 'ငွေဖြည့်မည်',
      logOut: 'အကောင့်ထွက်မည်',
      historyOrdersTab: 'အော်ဒါမှတ်တမ်း',
      historyTopupsTab: 'ငွေဖြည့်မှတ်တမ်း',
      noOrdersYet: 'အော်ဒါများ မရှိသေးပါ',
      noTopupsYet: 'ငွေဖြည့်မှတ်တမ်း မရှိသေးပါ',
      playerLabel: 'ကစားသမား',
      changePasswordTitle: 'စကားဝှက် ပြောင်းလဲရန်',
    },
    contactAdmin: {
      title: 'Admin ကို ဆက်သွယ်ရန်',
      otpRequired: 'OTP လိုအပ်ပါသည်',
      otpMessage: 'OTP ပေးရန်လိုအပ်ပါသည်။ အောက်ပါlinkများမှတစ်ဆင့် admin ကိုဆက်သွယ်ပေးပါ။',
    },
    mlChecker: {
      title: 'ML Region စစ်ဆေးရန်',
      gameId: 'ဂိမ်း ID',
      zoneId: 'ဆာဗာ ID',
      checkRegion: 'Region စစ်ဆေးမည်',
      nickname: 'Nickname',
      region: 'ဒေသ',
      lastLogin: 'နောက်ဆုံး ဝင်ရောက်ချိန်',
      found: 'တွေ့ရှိမှု',
      yes: 'ရှိသည်',
      no: 'မရှိပါ',
    },
    gameDetail: {
      notFound: 'ဝန်ဆောင်မှု မတွေ့ရှိပါ',
      topupAvailable: 'ငွေဖြည့်နိုင်ပါသည်',
      instantDelivery: 'မစောင့်ရ',
      estimatedDelivery: '3 မိနစ်',
      enterAccountDetails: 'အကောင့် အချက်အလက်များ ထည့်ပါ',
      accountDetailsCopy: 'Game ID နှင့် Server ID ကို သင့်ဂိမ်းပရိုဖိုင်တွင် ကြည့်ရှုနိုင်ပါသည်။',
      selectPackage: 'ပက်ကေ့ချ် ရွေးချယ်ပါ',
      selectPackageCopy: 'ဝယ်ယူလိုသော ပက်ကေ့ချ်ကို ရွေးချယ်ပေးပါ',
      couldntLoadPackages: 'ပက်ကေ့ချ်များ မတင်နိုင်ပါ',
      noPackages: 'ဝယ်ယူနိုင်သော ပက်ကေ့ချ်များ မရှိသေးပါ။ နောက်မှ ပြန်လည် စစ်ဆေးပေးပါ',
      goBack: 'နောက်သို့',
      checkAccount: 'အကောင့် စစ်ဆေးမည်',
      purchase: 'ဝယ်ယူမည်',
      buyNow: 'ယခု ဝယ်ယူမည်',
      verifyingAccount: 'အကောင့် စစ်ဆေးနေသည်...',
      processingOrder: 'အော်ဒါ တင်နေသည်...',
      confirmOrder: 'အော်ဒါ အတည်ပြုရန်',
      reviewOrder: 'အော်ဒါ စစ်ဆေးရန်',
      reviewOrderCopy: 'အတည်ပြုမီ အော်ဒါအချက်အလက်များကို စစ်ဆေးပေးပါ',
      placeOrder: 'အော်ဒါ တင်မည်',
      confirmPurchase: 'ဝယ်ယူမှု အတည်ပြုမည်',
      purchaseCompleted: 'ဝယ်ယူမှု အောင်မြင်ပါသည်!',
      purchaseFailed: 'ဝယ်ယူမှု မအောင်မြင်ပါ',
      processingTitle: 'လုပ်ဆောင်နေသည်',
      processingMessage: 'Your order is being processed. Check your history for updates.',
      missingField: (field: string) => `${field} ကို ထည့်သွင်းပေးပါ`,
      invalidProduct: 'Product မှားယွင်းနေပါသည်။ ပြန်လည် ကြိုးစားကြည့်ပါ',
      accountNotFoundTitle: 'အကောင့် မတွေ့ရှိပါ',
      accountNotFound: 'အကောင့် မတွေ့ရှိပါ။ ID များကို ပြန်လည် စစ်ဆေးပါ',
      otpRequiredMessage: 'Order တင်ပြီးတာနဲ့ Otp ပေးရန် Admin ဆီ ဆက်သွယ်ပါ။',
      placeholders: {
        gameid: 'ဥပမာ 12345678',
        zoneid: 'ဥပမာ 1234',
        gmail: 'သင့်ဂျီမေးထည့်ပါ',
        userid: 'အသုံးပြုသူ ID ထည့်ပါ',
        uid: 'UID ထည့်ပါ',
        contact: 'ဆက်သွယ်ရန်အကောင့်ထည့်ပါ (tg သို့မဟုတ် viber)',
        eamail: 'သင့် EA အကောင့် အီးမေးလ် ထည့်ပါ',
        supermail: 'သင့် Supercell ID အီးမေးလ် ထည့်ပါ',
         hi: 'Enter your hunter ID',
        generic: (label: string) => `သင့် ${label} ထည့်ပါ`,
      },
    },
    privacy: {
      title: 'ကိုယ်ရေးအချက်အလက် မူဝါဒ',
      sections: [
        '၁။ နိဒါန်း\nAMT-TopUp Services မှ ကြိုဆိုပါသည်။ ဤကိုယ်ရေးအချက်အလက် လုံခြုံမှုဆိုင်ရာ မူဝါဒသည် App အသုံးပြုစဉ် သင်၏ အချက်အလက်များကို မည်သို့ ကောက်ယူ၊ အသုံးပြု၊ ကာကွယ်သည်ကို ရှင်းလင်းဖော်ပြထားပါသည်။',
        '၂။ အချက်အလက်များ ကောက်ယူခြင်း\nဝန်ဆောင်မှုများ ပေးနိုင်ရန်အတွက် အောက်ပါအချက်အလက်များကို လိုအပ်သလို စီမံဆောင်ရွက်ပါသည်။',
        '၂.၁ အကောင့်အတည်ပြုခြင်း\n- ဖုန်းနံပါတ်နှင့် စကားဝှက်\n- အကောင့်အတည်ပြုချက်များ\n- အခြေခံ ပရိုဖိုင် အချက်အလက် (အမည်)\n- လုံခြုံရေးဆိုင်ရာ စက်ပစ္စည်း အချက်အလက်များ',
        '၂.၂ ငွေလွှဲခြင်းနှင့် ဝယ်ယူမှုများ\n- ငွေဖြည့်မှတ်တမ်း\n- အော်ဒါမှတ်တမ်း\n- ငွေပေးချေမှု အထောက်အထားများ (Screenshot)',
        '၃။ အချက်အလက် အသုံးပြုပုံ\n- အကောင့် စီမံခန့်ခွဲရန်\n- ငွေဖြည့်ခြင်းနှင့် ဝယ်ယူမှုများ ဆောင်ရွက်ရန်\n- ဂိမ်းပစ္စည်းများ ပို့ဆောင်ရန်\n- ဝန်ဆောင်မှု အကူအညီများ ပေးရန်\n- လုံခြုံရေးနှင့် လိမ်လည်မှုများ တားဆီးရန်',
        '၄။ ပြန်လည်ရောင်းချမှု (Reseller) ဆိုင်ရာ အသိပေးချက်\nကျွန်ုပ်တို့သည် တရားဝင် ဖြန့်ဖြူးသူများမှတစ်ဆင့် ဂိမ်းပစ္စည်းများကို ရယူရောင်းချပေးသော Reseller ဖြစ်ပါသည်။',
        '၅။ ငွေပေးချေမှုများ\nငွေပေးချေမှုအားလုံးသည် App ပြင်ပတွင်သာ ဆောင်ရွက်ရမည်ဖြစ်ပြီး၊ တင်ပေးသော Screenshot များကို ငွေပေးချေမှု စစ်ဆေးရန်သာ အသုံးပြုပါသည်။',
        '၆။ မူပိုင်ခွင့်\nဂိမ်း Logo, Banner နှင့် ပုံများအားလုံးသည် သက်ဆိုင်ရာ ပိုင်ရှင်များ၏ မူပိုင်ခွင့်သာ ဖြစ်ပါသည်။',
        '၇။ အသုံးပြုသူ အခွင့်အရေး\nသင်သည် မိမိအကောင့်ကို ဖျက်သိမ်းခြင်း၊ သင့်အချက်အလက်များကို ကြည့်ရှုခြင်း သို့မဟုတ် ပြင်ဆင်ခြင်းများ ပြုလုပ်နိုင်ပါသည်။',
        '၈။ လုံခြုံရေး\nသင့်အချက်အလက်များကို ကာကွယ်ရန်အတွက် ခိုင်မာသော လုံခြုံရေးစနစ်များနှင့် Encryption များကို အသုံးပြုထားပါသည်။',
        '၉။ ဆက်သွယ်ရန်\nဤမူဝါဒနှင့် ပတ်သက်၍ မေးမြန်းလိုပါက ကျွန်ုပ်တို့ထံ ဆက်သွယ်ပါ။',
      ],
    },
    maintenance: {
      loading: 'လုပ်ဆောင်နေသည်',
      retry: 'ထပ်စမ်းမည်',
    },
    spin: {
      title: 'ကံစမ်းမဲ',
      loading: 'တင်နေသည်...',
      spinsAvailable: 'ကံစမ်းခွင့် {count} ကြိမ် ရှိသည်',
      spinNow: 'ယခု လှည့်မည်',
      spinning: 'လှည့်နေသည်...',
      noSpins: 'ကံစမ်းခွင့် မရှိသေးပါ',
      earnSpins: '{amount} MMK နှင့်အထက် ငွေဖြည့်ပါက ကံစမ်းခွင့် ရရှိမည်!',
      newBalance: 'လက်ကျန်ငွေ အသစ်',
      youWon: 'သင် ရရှိသည်မှာ',
      betterLuckNextTime: 'နောက်တစ်ကြိမ်မှ ပြန်လည် ကြိုးစားပေးပါ!',
      spinRemaining: (count: number) => `ကံစမ်းရန် ${count} ကြိမ် ကျန်ပါသည်`,
      noSpinsRemaining: 'ကံစမ်းခွင့် ကုန်သွားပါပြီ',
      spinAgain: 'ထပ်မံ လှည့်မည်',
    },
    devices: {
      title: 'စက်ပစ္စည်းများ စီမံခန့်ခွဲမှု',
      subtitle: 'ချိတ်ဆက်ထားသော ဖုန်းများကို စီမံခန့်ခွဲပါ',
      loading: 'ဖုန်းများ တင်နေသည်...',
      noDevices: 'ဖုန်းများ မတွေ့ရှိပါ',
      currentDevice: 'လက်ရှိဖုန်း',
      added: 'ထည့်သွင်းခဲ့သည်',
      lastSeen: 'နောက်ဆုံးတွေ့ရှိချိန်',
      remove: 'ဖယ်ရှားမည်',
      infoText: 'ဖုန်းကို ဖယ်ရှားပါက အကောင့်မှ ထွက်ခွာသွားမည် ဖြစ်သည်။',
      confirmRemoveTitle: 'ဖုန်းဖယ်ရှားမည်လား?',
      confirmRemoveText: 'ဤဖုန်းသည် သင့်အကောင့်မှ ထွက်ခွာသွားမည် ဖြစ်သည်။ သေချာပါသလား?',
    },
    leaderboard: {
      title: 'အမှတ်ပေးဇယား',
      subtitle: 'ဒီလအတွင်း အများဆုံး ဝယ်ယူသူများ',
      loading: 'တင်နေသည်...',
      noData: 'ဒေတာ မရှိပါ',
    },
    chat: {
      title: 'Chat',
      comingSoon: 'မကြာမီလာမည်',
      description: 'ချတ်တ်အတွက်သီးခြားဖွဲ့စည်းမှုများကို မကြာခင်ရယူပါမည်။',
      supportTitle: 'ထောက်ခံပေးခြင်း ချတ်တ်',
      online: 'အွန်လိုင်း',
      offline: 'အွန်လိုင်းမဟုတ်',
      connecting: 'ချိတ်ဆက်နေသည်...',
      reconnecting: 'ပြန်ချိတ်ဆက်နေသည်...',
      ticket: 'ticket',
      typeMessage: 'မက်ဆေ့ချ် ရိုက်ထည့်ပါ...',
      startConversation: 'စကားဝိုင်းစတင်ပါ',
      supportGreeting: 'အကူအညီကရယူရန်ဆက်သွယ်လိုက်ပါ',
      loginRequired: 'လော့ဂ်အင်ဝင်ရန်လိုအပ်သည်',
      loginMessage: 'အကူအညီရယူရန် ကျေးဇူးပြု၍ လော့ဂ်အင်ဝင်ပါ',
    },
  },
} satisfies Record<Language, Record<string, unknown>>;

type Translation = typeof translations.en;

const I18nContext = createContext<{
  language: Language;
  setLanguage: (language: Language) => void;
  t: Translation;
} | null>(null);

function getInitialLanguage(): Language {
  return 'en';
}

export function I18nProvider({ children }: PropsWithChildren) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  useEffect(() => {
    try {
      window.localStorage.setItem(LANGUAGE_KEY, 'en');
    } catch {

    }
  }, []);

  const value = useMemo(
    () => ({
      language,
      setLanguage: () => setLanguageState('en'),
      t: translations.en as Translation,
    }),
    [language],
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error('useI18n must be used within I18nProvider');
  return ctx;
}

export function fieldLabel(t: Translation, field: string): string {
  const key = field.toLowerCase() as keyof typeof translations.en.common.fields;
  return t.common.fields[key] ?? field.toUpperCase();
}
